import { useEffect, useMemo, useState } from 'react';
import Btn from './ui/Btn';
import { ApiError, api } from '../lib/api';
import { toast } from '../stores/toaster';
import { AuditLogEntry } from '../lib/types';

/**
 * Onglet « Journal » du hub dossier (M3 / Sprint 9 (1) / ADR-052).
 *
 * Lecture seule du log d'audit append-only hash-chained scopé au
 * dossier. Sprint 9 (1) hooke seulement les events critiques
 * (dossier create/delete/restore, annotation create/update/delete,
 * pdf upload/delete). Sprint 9 (2) étendra aux modules restants
 * + offrira un bouton « vérifier l'intégrité de la chaîne ».
 */

const ACTION_LABELS: Record<string, string> = {
  'dossier.create': 'Dossier créé',
  'dossier.soft_delete': 'Dossier marqué pour suppression',
  'dossier.restore': 'Dossier restauré',
  'annotation.create': 'Annotation créée',
  'annotation.update': 'Annotation modifiée',
  'annotation.delete': 'Annotation supprimée',
  'pdf.upload': 'Pièce PDF uploadée',
  'pdf.delete': 'Pièce PDF supprimée',
  'partie.create': 'Partie créée',
  'partie.update': 'Partie modifiée',
  'partie.delete': 'Partie supprimée',
  'timeline.create': 'Évènement timeline créé',
  'timeline.update': 'Évènement timeline modifié',
  'timeline.delete': 'Évènement timeline supprimé',
  'tag.create': 'Tag créé',
  'tag.update': 'Tag modifié',
  'tag.delete': 'Tag supprimé',
  'note.create': 'Note créée',
  'note.update': 'Note modifiée',
  'note.delete': 'Note supprimée',
  'inventaire.create': 'Entrée inventaire créée',
  'inventaire.update': 'Entrée inventaire modifiée',
  'inventaire.delete': 'Entrée inventaire supprimée',
  'piece_client.create': 'Pièce client créée',
  'piece_client.update': 'Pièce client modifiée',
  'piece_client.delete': 'Pièce client supprimée',
  'auth.login': 'Connexion',
  'auth.logout': 'Déconnexion',
  'auth.mfa_challenge': 'MFA · challenge émis',
  'auth.mfa_verify': 'MFA · code vérifié',
  'auth.mfa_setup': 'MFA · configuration initiale',
  'auth.mfa_enable': 'MFA · activation confirmée',
  'auth.password_change': 'Mot de passe changé',
  'export.trigger': 'Export Word déclenché',
  'pdf.extract_dates': 'Dates extraites par IA',
  'pdf.extract_entities': 'Parties extraites par IA',
  'pdf.ocr': 'OCR Tesseract',
  'piece_client.upload': 'Fichier joint à une pièce client',
  'piece_client.ocr': 'OCR · pièce client',
  'piece_client.extract_dates': 'Dates IA · pièce client',
  'piece_client.extract_entities': 'Parties IA · pièce client',
  'export.complete': 'Export Word terminé',
};

const ACTION_BADGE: Record<string, string> = {
  'dossier.create': 'bg-cat-fav-pastel text-cat-fav',
  'dossier.soft_delete': 'bg-cat-def-pastel text-cat-def',
  'dossier.restore': 'bg-cat-fav-pastel text-cat-fav',
  'annotation.create': 'bg-accent-light text-accent-text',
  'annotation.update': 'bg-cat-imp-pastel text-cat-imp-text',
  'annotation.delete': 'bg-cat-def-pastel text-cat-def',
  'pdf.upload': 'bg-cat-fav-pastel text-cat-fav',
  'pdf.delete': 'bg-cat-def-pastel text-cat-def',
};

const MONTH_LABELS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
];

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const day = d.getDate();
  const month = MONTH_LABELS[d.getMonth()]!;
  const year = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${day} ${month} ${year} · ${hh}:${mm}`;
}

function dayKey(iso: string): string {
  return iso.slice(0, 10); // YYYY-MM-DD
}

function dayHeader(key: string): string {
  const [y, m, d] = key.split('-');
  return `${parseInt(d!, 10)} ${MONTH_LABELS[parseInt(m!, 10) - 1]} ${y}`;
}

type Props = {
  dossierId: string;
};

export default function JournalTab({ dossierId }: Props) {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<boolean | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const rows = await api.get<AuditLogEntry[]>(`/dossiers/${dossierId}/audit?limit=500`);
        if (cancelled) return;
        setEntries(rows);
        setHasMore(rows.length === 500);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setError(
          err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [dossierId]);

  const loadMore = async () => {
    if (loadingMore || entries.length === 0) return;
    setLoadingMore(true);
    try {
      const cursor = entries[entries.length - 1]!.id;
      const rows = await api.get<AuditLogEntry[]>(
        `/dossiers/${dossierId}/audit?limit=500&before_id=${cursor}`
      );
      setEntries((prev) => [...prev, ...rows]);
      setHasMore(rows.length === 500);
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setLoadingMore(false);
    }
  };

  const grouped = useMemo(() => {
    const m = new Map<string, AuditLogEntry[]>();
    for (const e of entries) {
      const k = dayKey(e.created_at);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(e);
    }
    return m;
  }, [entries]);

  const dayKeys = useMemo(() => Array.from(grouped.keys()).sort().reverse(), [grouped]);

  const verifyChain = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const resp = await api.get<{ valid: boolean }>(`/dossiers/${dossierId}/audit/verify`);
      setVerifyResult(resp.valid);
      if (resp.valid) {
        toast.success('Chaîne vérifiée — aucune altération détectée.');
      } else {
        toast.error('Chaîne CASSÉE — une entrée a été modifiée hors-canal.');
      }
    } catch (err) {
      toast.error(
        err instanceof ApiError ? (err.problem.detail ?? err.problem.title) : 'Erreur réseau'
      );
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-rule bg-paper-elevated p-3">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[12px] text-text-secondary">
            <strong className="font-medium text-text-primary">{entries.length}</strong> entrée
            {entries.length > 1 ? 's' : ''} d'audit
          </div>
          <div className="flex items-center gap-2">
            {verifyResult !== null && (
              <span
                className={`inline-flex items-center rounded-pill px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] ${
                  verifyResult ? 'bg-cat-fav-pastel text-cat-fav' : 'bg-cat-def-pastel text-cat-def'
                }`}
                title={verifyResult ? 'Chaîne intacte' : 'Une entrée a été modifiée hors-canal'}
              >
                {verifyResult ? '✓ chaîne intacte' : '⚠ chaîne cassée'}
              </span>
            )}
            <Btn
              variant="secondary"
              size="sm"
              onClick={() => void verifyChain()}
              disabled={verifying}
              title="Recalcule chaque hash de la chaîne audit (peut prendre quelques secondes)"
            >
              {verifying ? 'Vérification…' : 'Vérifier la chaîne'}
            </Btn>
            <span className="font-mono text-[10px] uppercase tracking-[0.3px] text-text-tertiary">
              Append-only · hash-chained
            </span>
          </div>
        </div>
        <p className="mt-1.5 text-[11px] text-text-tertiary">
          Sprint 9 (2) : audit hooké sur tous les modules CRUD. Vérification d'intégrité disponible
          (recompute SHA-256 row-par-row).
        </p>
      </div>

      {error && (
        <p className="rounded-md border border-cat-def/40 bg-cat-def-pastel px-3 py-2 text-sm text-cat-def">
          {error}
        </p>
      )}

      {loading ? (
        <p className="text-sm text-text-secondary">Chargement…</p>
      ) : entries.length === 0 ? (
        <p className="rounded-md border border-dashed border-rule-strong bg-paper-card p-10 text-center text-sm text-text-secondary">
          Aucune entrée d'audit pour ce dossier. Les actions futures (créer une annotation, uploader
          une pièce, supprimer le dossier…) apparaîtront ici en temps réel.
        </p>
      ) : (
        <div className="space-y-5">
          {dayKeys.map((dk) => {
            const rows = grouped.get(dk)!;
            return (
              <section key={dk} className="space-y-2">
                <header className="border-b border-rule-strong pb-1">
                  <h3 className="font-serif text-[15px] font-medium text-text-primary">
                    {dayHeader(dk)} <span className="text-text-tertiary">· {rows.length}</span>
                  </h3>
                </header>
                <ul className="space-y-1.5 border-l-2 border-rule pl-4">
                  {rows.map((e) => (
                    <li key={e.id} className="relative">
                      <span
                        className="absolute -left-[21px] top-2.5 inline-flex h-2.5 w-2.5 rounded-full border-2 border-accent bg-paper-elevated"
                        aria-hidden
                      />
                      <div className="rounded-sm border border-rule bg-paper-elevated p-3 hover:bg-paper-card">
                        <div className="flex flex-wrap items-baseline gap-2">
                          <span
                            className={`inline-flex items-center rounded-pill px-2 py-[2px] text-[10px] font-medium uppercase tracking-[0.3px] ${
                              ACTION_BADGE[e.action] ?? 'bg-paper-subtle text-text-secondary'
                            }`}
                          >
                            {ACTION_LABELS[e.action] ?? e.action}
                          </span>
                          <span className="font-mono text-[11px] text-text-tertiary">
                            {formatTimestamp(e.created_at)}
                          </span>
                          {e.actor_display_name && (
                            <span className="text-[11px] text-text-secondary">
                              par{' '}
                              <span className="font-medium text-text-primary">
                                {e.actor_display_name}
                              </span>
                            </span>
                          )}
                          {e.ip_address && (
                            <span className="font-mono text-[10px] text-text-tertiary">
                              {e.ip_address}
                            </span>
                          )}
                        </div>
                        {e.metadata && Object.keys(e.metadata).length > 0 && (
                          <div className="mt-1.5 font-mono text-[11px] text-text-secondary">
                            {Object.entries(e.metadata).map(([k, v]) => (
                              <span key={k} className="mr-3">
                                <span className="text-text-tertiary">{k}:</span>{' '}
                                <span>{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
          {hasMore && (
            <div className="flex justify-center pt-2">
              <Btn variant="ghost" size="sm" onClick={() => void loadMore()} disabled={loadingMore}>
                {loadingMore ? 'Chargement…' : 'Charger 500 entrées de plus'}
              </Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
